/**
 * AI-GENERATED CODE - DirectionsTooltipPositioning.ts
 *
 * This module finds optimal positions for route tooltip markers on Google Maps.
 * Generated by Claude AI to place route information tooltips at non-overlapping
 * positions along their respective routes.
 *
 * WHAT THIS CODE DOES:
 * --------------------
 * Analyzes multiple routes on a map and finds the best position to place a tooltip
 * for each route where it won't overlap with other routes. This ensures tooltips
 * are placed at clear, visible locations along their routes.
 *
 * HOW IT WORKS:
 * -------------
 * 1. Route Analysis:
 *    - Takes a route and compares it against all other routes
 *    - Identifies segments where routes overlap or are too close
 *    - Finds segments that are unique to each route
 *
 * 2. Position Selection:
 *    - Looks for the longest non-overlapping segment of each route
 *    - Places the tooltip at the center of that segment
 *    - Falls back to the least overlapped point if no clear segment exists
 *
 * 3. Natural Positioning:
 *    - Returns geographic coordinates (LatLng) for tooltip placement
 *    - Tooltips are anchored to these coordinates and move naturally with the map
 *    - No CSS transforms or manual repositioning needed
 *
 * KEY PARAMETERS:
 * ---------------
 * - threshold: 0.00008 - Distance threshold for considering points as overlapping
 * - sampleInterval: path.length / 20 - How many points to sample when searching
 *
 * INTEGRATION:
 * ------------
 * Used by the Directions component to position AdvancedMarker tooltips
 * at optimal locations along each route where they won't overlap with
 * other routes, ensuring clear visibility of route information.
 *
 * @module DirectionsTooltipPositioning
 * @author Claude AI
 * @date 2024
 */

interface DirectionsData {
  routes: Array<{
    overview_path: google.maps.LatLng[];
  }>;
}

/**
 * Finds a non-overlapping position along a route to place its tooltip.
 * Analyzes the route against other routes to find a clear segment where
 * the tooltip can be placed without overlapping other route paths.
 */
export const findNonOverlappingPosition = (
  route: { overview_path?: google.maps.LatLng[] },
  routeIndex: number,
  directions: DirectionsData,
): google.maps.LatLng | null => {
  const path = route.overview_path;
  if (!path || path.length === 0) return null;

  // Get all other routes
  const otherRoutes = directions.routes
    .filter((_, idx) => idx !== routeIndex)
    .map((r) => r.overview_path)
    .filter((p) => p && p.length > 0);

  // Helper function to check if a point is near any segment of a route
  const isPointNearRoute = (
    point: google.maps.LatLng,
    routePath: google.maps.LatLng[],
    threshold = 0.00008,
  ): boolean => {
    for (let i = 0; i < routePath.length - 1; i++) {
      const p1 = routePath[i];
      const p2 = routePath[i + 1];

      // Get lat/lng values handling both LatLng objects and plain objects
      const pointLat = typeof point.lat === 'function' ? point.lat() : (point as unknown as { lat: number }).lat;
      const pointLng = typeof point.lng === 'function' ? point.lng() : (point as unknown as { lng: number }).lng;
      const p1Lat = typeof p1.lat === 'function' ? p1.lat() : (p1 as unknown as { lat: number }).lat;
      const p1Lng = typeof p1.lng === 'function' ? p1.lng() : (p1 as unknown as { lng: number }).lng;
      const p2Lat = typeof p2.lat === 'function' ? p2.lat() : (p2 as unknown as { lat: number }).lat;
      const p2Lng = typeof p2.lng === 'function' ? p2.lng() : (p2 as unknown as { lng: number }).lng;

      // Calculate distance from point to line segment
      const A = pointLat - p1Lat;
      const B = pointLng - p1Lng;
      const C = p2Lat - p1Lat;
      const D = p2Lng - p1Lng;

      const dot = A * C + B * D;
      const lenSq = C * C + D * D;

      if (lenSq === 0) continue;

      let param = dot / lenSq;
      param = Math.max(0, Math.min(1, param));

      const nearestLat = p1Lat + param * C;
      const nearestLng = p1Lng + param * D;

      const dist = Math.sqrt(Math.pow(pointLat - nearestLat, 2) + Math.pow(pointLng - nearestLng, 2));

      if (dist < threshold) return true;
    }
    return false;
  };

  // Find non-overlapping segments
  const segments: { start: number; end: number }[] = [];
  let segmentStart: number | null = null;

  for (let i = 0; i < path.length; i++) {
    const point = path[i];
    let overlaps = false;

    // Check if this point overlaps with any other route
    for (const otherPath of otherRoutes) {
      if (isPointNearRoute(point, otherPath)) {
        overlaps = true;
        break;
      }
    }

    if (!overlaps) {
      // Point doesn't overlap - start or continue segment
      if (segmentStart === null) {
        segmentStart = i;
      }
    } else {
      // Point overlaps - end current segment if exists
      if (segmentStart !== null) {
        segments.push({ start: segmentStart, end: i - 1 });
        segmentStart = null;
      }
    }
  }

  // Close final segment if it extends to the end
  if (segmentStart !== null) {
    segments.push({ start: segmentStart, end: path.length - 1 });
  }

  // If we found non-overlapping segments
  if (segments.length > 0) {
    // Find the longest segment
    let longestSegment = segments[0];
    let longestLength = longestSegment.end - longestSegment.start;

    for (const segment of segments) {
      const length = segment.end - segment.start;
      if (length > longestLength) {
        longestLength = length;
        longestSegment = segment;
      }
    }

    // Return the center of the longest non-overlapping segment
    const centerIndex = Math.floor((longestSegment.start + longestSegment.end) / 2);
    return path[centerIndex];
  }

  // Fallback: if no non-overlapping segments, try to find the least overlapped point
  let bestIndex = Math.floor(path.length / 2);
  let minOverlapCount = Infinity;

  // Sample points along the route
  const sampleInterval = Math.max(1, Math.floor(path.length / 20));

  for (let i = 0; i < path.length; i += sampleInterval) {
    const point = path[i];
    let overlapCount = 0;

    for (const otherPath of otherRoutes) {
      if (isPointNearRoute(point, otherPath, 0.00005)) {
        overlapCount++;
      }
    }

    if (overlapCount < minOverlapCount) {
      minOverlapCount = overlapCount;
      bestIndex = i;
    }
  }

  return path[bestIndex];
};
